// src/componentes/SubscriptionModal/index.js
import { useContext, useState } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import CardPaymentBrick from '../CardPaymentBrick';
import { Context } from '../../context/UserContext';
import api from '../../utils/api';
import styles from './styles.module.css';

/*
    Modal único de checkout de assinatura. Cobre dois cenários:

    1) Usuário já tem conta (ex: upgrade em /plans) → passa só `plan` e
       `payerEmail`. Ao tokenizar o cartão, chama subscribe() direto.

    2) Usuário ainda NÃO tem conta (ex: cadastro em /register) → passa
       também `preRegisterData` com os dados do formulário. Nesse caso:
       - NENHUMA conta existe quando o modal abre.
       - Ao tokenizar o cartão, chama a rota atômica
         /subscriptions/register-and-subscribe, que tenta o pagamento
         PRIMEIRO e só cria a conta se vier autorizado.
       - Se o pagamento falhar, NENHUMA conta é criada — nem free.
       - Se fechar o modal a qualquer momento (antes de pagar OU depois
         de uma recusa), simplesmente não sobra nada no banco.

    Props:
      plan             { planName, name, amount }
      payerEmail       string (usado só no cenário 1)
      preRegisterData  objeto do formulário de cadastro (cenário 2)
      onClose          function() — chamado ao fechar sem sucesso
      onSuccess        function() — chamado após assinatura autorizada
*/
export default function SubscriptionCheckoutModal({ plan, payerEmail, preRegisterData, onClose, onSuccess }) {
    const { subscribe, authUser } = useContext(Context);
    const [status, setStatus]     = useState('form'); // form | loading | success | error
    const [errorMsg, setErrorMsg] = useState('');

    const isPreRegisterFlow = !!preRegisterData;

    async function handleTokenReady(cardTokenId) {
        setStatus('loading');
        setErrorMsg('');

        try {
            if (isPreRegisterFlow) {
                // Cenário 2: rota atômica — tenta o pagamento primeiro,
                // só cria a conta se vier autorizado. Se der erro,
                // o catch abaixo trata — e NADA foi criado.
                const { data } = await api.post('/subscriptions/register-and-subscribe', {
                    ...preRegisterData,
                    planName: plan.planName,
                    cardTokenId,
                });

                // data tem o mesmo formato que register()/login() retornam
                // (token, etc.) — usa isso pra autenticar o usuário agora
                // que a conta acabou de ser criada com sucesso.
                await authUser(data);

                setStatus('success');
                setTimeout(() => onSuccess?.(), 1200);
                return;
            }

            // Cenário 1: usuário já tem conta — fluxo normal de upgrade
            const savedToken = localStorage.getItem('token');
            const token = savedToken ? JSON.parse(savedToken) : null;
            const result = await subscribe(token, plan.planName, cardTokenId);

            if (result?.authorized) {
                setStatus('success');
                setTimeout(() => onSuccess?.(), 1200);
                return;
            }

            setStatus('error');
            setErrorMsg('Não foi possível autorizar o pagamento. Verifique os dados do cartão.');
        } catch (err) {
            console.error('Erro no checkout de assinatura:', err);
            setStatus('error');
            setErrorMsg(
                err.response?.data?.message ||
                (isPreRegisterFlow
                    ? 'Pagamento não autorizado. Nenhuma conta foi criada — você pode tentar novamente ou fechar e escolher o plano Gratuito.'
                    : 'Não foi possível processar. Verifique os dados e tente novamente.')
            );
        }
    }

    function handleBrickError(msg) {
        setStatus('error');
        setErrorMsg(msg);
    }

    const effectivePayerEmail = isPreRegisterFlow ? preRegisterData.email : payerEmail;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* Sempre pode fechar — nesse fluxo, nenhuma conta é
                    criada até o pagamento ser aprovado, então fechar
                    nunca deixa rastro nenhum no banco. */}
                {status !== 'loading' && (
                    <button className={styles.closeBtn} onClick={() => onClose?.()} aria-label="Fechar">
                        <X size={18} />
                    </button>
                )}

                <h2 className={styles.title}>Assinar {plan.name}</h2>
                <p className={styles.subtitle}>
                    {plan.amount != null ? `R$ ${plan.amount.toFixed(2).replace('.', ',')}/mês` : ''}
                    {isPreRegisterFlow
                        ? ' — sua conta só é criada após a confirmação do pagamento'
                        : ' — cobrança recorrente via Mercado Pago'}
                </p>

                {status === 'success' && (
                    <div className={styles.successBox}>
                        <CheckCircle2 size={32} />
                        <p>{isPreRegisterFlow ? 'Conta criada e assinatura ativada com sucesso!' : 'Assinatura ativada com sucesso!'}</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className={styles.errorBox}>
                        <AlertCircle size={18} />
                        <span>{errorMsg}</span>
                    </div>
                )}

                {status === 'loading' && (
                    <div className={styles.loadingBox}>
                        <Loader2 size={20} className={styles.spinner} />
                        <span>{isPreRegisterFlow ? 'Processando pagamento...' : 'Processando pagamento...'}</span>
                    </div>
                )}

                {(status === 'form' || status === 'error') && (
                    <CardPaymentBrick
                        amount={plan.amount}
                        payerEmail={effectivePayerEmail}
                        onTokenReady={handleTokenReady}
                        onError={handleBrickError}
                    />
                )}
            </div>
        </div>
    );
}
